import { withZod } from '@remix-validated-form/with-zod'
import { ReactElement, useState } from 'react'
import { z } from 'zod'
import Button from './Button'
import FormCombo from './form/FormCombo'
import FormComboAsyncV2 from './form/FormComboAsyncV2'
import { FormMarkDownEditor } from './form/FormMarkDownEditor'
import { FormRadioGroup } from './form/FormRadioGroup'
import { SmartForm } from './form/SmartForm'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { OptionType } from 'types/OptionType'
import { usersFetcher } from 'app/lib/fetchers/usersFetcher'

export type ClosedLostFormData = {
  contactId: string
  owner: { id: string; name: string }
  closedLostReason: string
  disqualified?: string
  note?: string
  description: string
}

export const closedLostValidationSchema = withZod(
  z.object({
    contactId: z.string().min(1, { message: `Please provide a contactId` }),
    // FormComboAsyncV2 submits the whole option as `owner[id]`/`owner[name]`/`owner[value]`.
    // Plain strings stay rejected so the owner id is never silently dropped.
    owner: z
      .object({ id: z.string(), name: z.string(), value: z.string().optional() })
      .transform((owner) => ({ id: owner.id, name: owner.name })),
    closedLostReason: z.string().min(1, { message: `Please provide a reason` }),
    disqualified: z.string().optional(),
    note: z.string().optional(),
    description: z.string().min(1, { message: `Description should be Closed Lost` }),
  }),
)

export function ClosedLostForm({
  activity,
  contact,
  handleSubmit,
  handleCancel,
}: {
  activity?: ActivityJson
  contact: ContactJson
  handleSubmit: (data) => void
  handleCancel: () => void
}): ReactElement {
  const user = useUser()
  const [saving, setSaving] = useState<boolean>(false)
  const [closedLostReason, setClosedLostReason] = useState<OptionType>(() => {
    if (contact?.closedLostReason) {
      return { id: contact?.closedLostReason, value: contact?.closedLostReason, name: contact?.closedLostReason }
    }
    return { id: `-1`, value: ``, name: `` }
  })
  const [staff, setStaff] = useState<OptionType>(() => {
    if (activity?.owner?.id) {
      return { id: activity.owner.id, value: activity.owner.name, name: activity.owner.name }
    }
    return { id: user?.id ?? ``, value: user?.name ?? ``, name: user?.name ?? `` }
  })

  if (!user) return null as unknown as ReactElement

  const onSubmit = async (data) => {
    setSaving(true)
    handleSubmit(data)
  }
  return (
    <SmartForm
      id="closed-lost-form"
      className="w-[600px] !gap-y-3.5"
      hiddenInputs={[
        { name: `contactId`, value: contact.id },
        { name: `description`, value: `Closed Lost` },
      ]}
      validator={closedLostValidationSchema}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      hideButtons
      preventSubmit
    >
      <FormComboAsyncV2
        name="owner"
        label="Closed by"
        // usersFetcher scopes by workspaceId.keyword; the previous raw-param fetcher
        // sent a typo'd `worspaceId:` ES term and always returned zero users.
        fetcher={usersFetcher}
        inputClassName="!text-sm !font-normal !leading-5 !text-default"
        labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
        value={staff}
        additionalParams={{ workspaceId: contact.workspaceId! }}
        optionsMapper={(options) => {
          if (options.total > 0) {
            return options.hits.map((option) => ({ id: option.id, name: option.name, value: option.id }))
          }
          return []
        }}
        onChange={(value) => setStaff(value)}
        cols={3}
      />
      <FormCombo
        value={closedLostReason}
        placeholder="Reason"
        label="Closed Lost Reason"
        name="closedLostReason"
        inputClassName="!text-sm !font-normal !leading-5 !text-default"
        labelClassName="mb-1.5 text-default text-sm font-medium leading-4 required"
        options={[
          `Insurance: State / HMO`,
          `Insurance: Out of Network`,
          `Schedule Availability`,
          `Pricing Too High`,
          `Unable to Contact`,
          `Service Not Offered`,
          `Office Too Far`,
          `Scheduled with Competitor`,
          `Other`,
        ].map((lostReason) => ({
          id: lostReason,
          name: lostReason,
          value: lostReason,
        }))}
        onChange={(value) => setClosedLostReason(value)}
        cols={3}
      />
      {!isSMCOrg(user.orgId) && (
        <div className="col-span-1">
          <FormRadioGroup
            label="Disqualified?"
            name="disqualified"
            defaultValue={activity ? (activity?.isQualified ? `false` : `true`) : `false`}
            className="text-sm font-normal leading-5 text-default"
            labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
            values={[
              { label: `Yes`, value: `true` },
              { label: `No`, value: `false` },
            ]}
          />
        </div>
      )}
      <FormMarkDownEditor
        name="note"
        label="Note"
        description={activity?.note || ``}
        richTextEditorNamespace="Add-Edit-Closed-Lost-Reason"
        labelClassName="mb-1.5 text-default text-sm font-medium leading-4"
        placeholder="Type your note here..."
        errorClassName="text-red-500"
        cols={6}
        editable
      />
      <div className="col-span-6 -mx-6 -mb-4 flex items-center justify-end gap-2 border-t border-default px-6 py-3">
        <Button label="Cancel" mode="white" size="md" onClick={handleCancel} />
        <Button type="submit" label="Save" mode="primary" size="md" loading={saving} />
      </div>
    </SmartForm>
  )
}
